import * as React from 'react';
import { MdAdd, MdAttachMoney, MdClose, MdDelete, MdEdit, MdLocalHospital, MdPercent, MdSearch } from 'react-icons/md';
import DataGrid from '../../Components/Table/DataGrid';
import DeleteModel from '../../Components/Common/DeleteModel';
import IncentiveRateFormModal from '../../Components/IncentiveRates/IncentiveRateFormModal';
import PageTitle from '../../Components/Common/PageTitle';
import {
	getAllIncentiveRatesForAccount,
	getIncentiveRateById,
	saveIncentiveRate,
	deleteIncentiveRate,
} from '../../Services/IncentiveRateService';
import { getAllOrganizationNames } from '../../Services/OrganizationService';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { toast } from 'react-toastify';

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export default function IncentiveRatesList() {
	const { token, permissions, decodedToken } = useAuth();
	const userPermissionCodes = React.useMemo(
		() => resolveUserPermissionCodes({ permissions, decodedToken }),
		[permissions, decodedToken],
	);

	const canCreate = permissionHas(userPermissionCodes, PermissionCodes.INCENTIVE_CONFIG_CREATE, decodedToken);
	const canEdit = permissionHas(userPermissionCodes, PermissionCodes.INCENTIVE_CONFIG_EDIT, decodedToken);
	const canDelete = permissionHas(userPermissionCodes, PermissionCodes.INCENTIVE_CONFIG_DELETE, decodedToken);

	const [organizations, setOrganizations] = React.useState([]);
	const [organizationsLoading, setOrganizationsLoading] = React.useState(false);

	const [rows, setRows] = React.useState([]);
	const [loading, setLoading] = React.useState(false);
	const [search, setSearch] = React.useState('');

	const [formModalOpen, setFormModalOpen] = React.useState(false);
	const [formModalLoading, setFormModalLoading] = React.useState(false);
	const [formInitialValues, setFormInitialValues] = React.useState({});

	const [deleteTarget, setDeleteTarget] = React.useState(null);
	const [deleteLoading, setDeleteLoading] = React.useState(false);
	const [deleteError, setDeleteError] = React.useState('');

	const loadOrganizations = React.useCallback(async () => {
		if (!token) return;
		try {
			setOrganizationsLoading(true);
			const response = await getAllOrganizationNames(token);
			const list = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
			setOrganizations(list);
		} catch (error) {
			console.error(error);
			toast.error('Unable to load organizations');
			setOrganizations([]);
		} finally {
			setOrganizationsLoading(false);
		}
	}, [token]);

	React.useEffect(() => {
		if (token) loadOrganizations();
	}, [token, loadOrganizations]);

	const loadRates = React.useCallback(async () => {
		if (!token) return;
		try {
			setLoading(true);
			const response = await getAllIncentiveRatesForAccount(token);
			if (response?.is_success) {
				setRows(Array.isArray(response?.data) ? response.data : []);
			} else if (response?.is_success === false && response?.status_code === 500) {
				toast.error('Internal server error. Please contact support.');
				setRows([]);
			} else {
				setRows([]);
			}
		} catch (error) {
			console.error(error);
			setRows([]);
			toast.error('Unable to load incentive rates');
		} finally {
			setLoading(false);
		}
	}, [token]);

	React.useEffect(() => {
		if (token) loadRates();
	}, [token, loadRates]);

	const handleCreate = React.useCallback(() => {
		setFormInitialValues({});
		setFormModalOpen(true);
	}, []);

	const handleEdit = React.useCallback(async (row) => {
		setFormModalLoading(true);
		try {
			const res = await getIncentiveRateById(token, row.id);
			if (res?.is_success) {
				setFormInitialValues(res.data);
				setFormModalOpen(true);
			} else {
				toast.error(res?.message || 'Failed to load incentive rate');
			}
		} catch (error) {
			toast.error('Failed to load incentive rate for edit');
		} finally {
			setFormModalLoading(false);
		}
	}, [token]);

	const handleFormSubmit = async (formData) => {
		setFormModalLoading(true);
		try {
			const payload = {
				...formData,
				type: formData.id ? 'update' : 'create',
			};
			const res = await saveIncentiveRate(token, payload);
			if (res?.is_success) {
				toast.success(res?.message || (formData.id ? 'Incentive rate updated' : 'Incentive rate added'));
				loadRates();
				setFormModalOpen(false);
			} else if (res?.status_code === 409) {
				toast.error(res?.message || 'A rate for this insurance, CPT code, and facility already exists.');
			} else {
				toast.error(res?.message || 'Failed to save incentive rate');
			}
		} catch (error) {
			toast.error('Failed to save incentive rate');
		} finally {
			setFormModalLoading(false);
		}
	};

	const handleDeleteClick = React.useCallback((row) => {
		setDeleteError('');
		setDeleteTarget(row);
	}, []);

	const handleDeleteClose = React.useCallback(() => {
		if (deleteLoading) return;
		setDeleteTarget(null);
		setDeleteError('');
	}, [deleteLoading]);

	const handleDeleteConfirm = React.useCallback(async () => {
		if (!deleteTarget?.id) return;
		try {
			setDeleteLoading(true);
			setDeleteError('');
			const response = await deleteIncentiveRate(token, deleteTarget.id);
			if (response?.is_success) {
				setRows((prev) => prev.filter((row) => row.id !== deleteTarget.id));
				setDeleteTarget(null);
				toast.success(response?.message || 'Incentive rate deleted successfully.');
			} else {
				setDeleteError(response?.message || 'Failed to delete incentive rate.');
			}
		} catch (error) {
			console.error(error);
			setDeleteError('Something went wrong while deleting the incentive rate.');
		} finally {
			setDeleteLoading(false);
		}
	}, [deleteTarget, token]);

	const filteredRows = React.useMemo(() => {
		const term = search.trim().toLowerCase();
		if (!term) return rows;
		return rows.filter((row) =>
			[row.organization_name, row.cpt_code, row.insurance, row.facility].some((value) =>
				(value || '').toLowerCase().includes(term),
			),
		);
	}, [rows, search]);

	const stats = React.useMemo(() => {
		const facilitySet = new Set(rows.map((row) => row.facility).filter((value) => value && value !== 'ALL'));
		const insuranceSet = new Set(rows.map((row) => row.insurance).filter((value) => value && value !== 'ALL'));
		return [
			{
				label: 'Total Rates',
				value: rows.length,
				icon: <MdPercent size={22} color="var(--hc-icon-blue-text)" />,
				iconBg: 'var(--hc-icon-blue-bg)',
				accentClass: 'border-primary',
				valueClass: 'text-primary',
			},
			{
				label: 'Insurance Overrides',
				value: insuranceSet.size,
				icon: <MdAttachMoney size={22} color="var(--hc-status-success-text)" />,
				iconBg: 'var(--hc-icon-green-bg)',
				accentClass: 'border-success',
				valueClass: 'text-success',
			},
			{
				label: 'Facility Overrides',
				value: facilitySet.size,
				icon: <MdLocalHospital size={22} color="var(--hc-status-warning-text)" />,
				iconBg: 'var(--hc-status-warning-bg)',
				accentClass: 'border-secondary',
				valueClass: 'text-secondary',
			},
		];
	}, [rows]);

	const columns = React.useMemo(
		() => [
			{
				id: 'organization_name',
				label: 'Organization',
				minWidth: 130,
				format: (value) => <span className="fw-semibold text-dark">{value || '—'}</span>,
			},
			{
				id: 'cpt_code',
				label: 'CPT Code',
				minWidth: 90,
				format: (value) => <span className="fw-semibold text-dark">{value}</span>,
			},
			{
				id: 'insurance',
				label: 'Insurance',
				minWidth: 100,
				format: (value) =>
					value && value !== 'ALL' ? value : <span className="text-secondary">All insurances</span>,
			},
			{
				id: 'facility',
				label: 'Facility',
				minWidth: 100,
				format: (value) =>
					value && value !== 'ALL' ? value : <span className="text-secondary">All facilities</span>,
			},
			{
				id: 'standard_rate',
				label: 'Standard Rate',
				minWidth: 90,
				align: 'right',
				format: (value) => (
					<span className="fw-semibold hc-text-primary">{currencyFormatter.format(Number(value) || 0)}</span>
				),
			},
			...(canEdit || canDelete ? [{
				id: 'actions',
				label: 'Actions',
				minWidth: 80,
				align: 'center',
				format: (_value, row) => (
					<div className="d-flex align-items-center justify-content-center gap-2">
						{canEdit ? (
							<button
								type="button"
								title="Edit"
								className="btn btn-sm p-1 lh-1 hc-text-primary border-0 rounded-2"
								style={{ backgroundColor: 'var(--hc-icon-blue-bg)' }}
								onClick={() => handleEdit(row)}
							>
								<MdEdit size={16} />
							</button>
						) : null}
						{canDelete ? (
							<button
								type="button"
								title="Delete"
								className="btn btn-sm p-1 lh-1 text-danger border-0 rounded-2"
								style={{ backgroundColor: 'var(--hc-status-danger-bg)' }}
								onClick={() => handleDeleteClick(row)}
							>
								<MdDelete size={16} />
							</button>
						) : null}
					</div>
				),
			}] : []),
		],
		[canEdit, canDelete, handleEdit, handleDeleteClick],
	);

	return (
		<>
			<PageTitle title="Config - Aegis 360" />
			<IncentiveRateFormModal
				open={formModalOpen}
				onClose={() => { if (!formModalLoading) setFormModalOpen(false); }}
				onSubmit={handleFormSubmit}
				loading={formModalLoading}
				initialValues={formInitialValues}
				organizations={organizations}
				organizationsLoading={organizationsLoading}
				token={token}
				title={formInitialValues?.id ? 'Edit Incentive Rate' : 'Add Incentive Rate'}
			/>

			<div className="d-flex flex-md-row flex-column align-items-start justify-content-between mb-4">
				<div>
					<h4 className="fw-bold mb-1 hc-text-primary fs-3">Config</h4>
					<p className="text-secondary mb-0 small">
						Configure the standard rate paid per CPT code, insurance, and facility for payroll incentives, across every organization.
					</p>
				</div>
				{canCreate ? (
					<button
						onClick={handleCreate}
						type="button"
						className="btn hc-bg-primary mt-md-0 mt-2 w-md-auto btn-sm text-white fw-semibold d-flex align-items-center justify-content-center gap-1 px-3 py-2 rounded-3 text-nowrap"
					>
						<MdAdd size={20} />
						<span>Add Incentive Rate</span>
					</button>
				) : null}
			</div>

			{loading ? (
				<div className="row g-3 mb-3">
					{[1, 2, 3].map((_, i) => (
						<div key={i} className="col-12 col-sm-6 col-lg-4">
							<div className="bg-white p-4 rounded-4 shadow">
								<div className="skeleton mb-2" style={{ height: 16, width: '60%' }} />
								<div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 28, width: '40%' }} />
							</div>
						</div>
					))}
				</div>
			) : (
				<div className="row g-3 mb-3">
					{stats.map((stat) => (
						<div key={stat.label} className="col-12 col-sm-6 col-lg-4">
							<div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow p-3 d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
								<div className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`} />
								<div
									className="d-inline-flex align-items-center justify-content-center rounded-3"
									style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}
								>
									{stat.icon}
								</div>
								<div className="d-flex flex-column fw-bold lh-sm">
									<span className="text-dark" style={{ fontSize: '0.85rem' }}>
										{stat.label}
									</span>
									<span className={`fw-bold fs-5 ${stat.valueClass}`}>
										{stat.value}
									</span>
								</div>
							</div>
						</div>
					))}
				</div>
			)}

			<div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
				<div className="row g-2 mb-3">
					<div className="col-12">
						<div className="position-relative">
							<span
								className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none"
								style={{ left: '12px' }}
							>
								<MdSearch size={18} />
							</span>
							<input
								type="text"
								className="form-control form-control-sm hc-input"
								placeholder="Search by organization, CPT code, insurance, or facility"
								value={search}
								onChange={(event) => setSearch(event.target.value)}
								style={{ paddingLeft: '36px', paddingRight: '36px', height: '40px' }}
							/>
							{search ? (
								<button
									type="button"
									aria-label="Clear search"
									onClick={() => setSearch('')}
									className="btn btn-link bg-danger p-0 position-absolute top-50 translate-middle-y text-light rounded-circle"
									style={{ right: '8px', lineHeight: 0 }}
								>
									<MdClose size={18} />
								</button>
							) : null}
						</div>
					</div>
				</div>
				<div className="bg-white p-3 rounded shadow">
					{loading ? (
						<div>
							{[...Array(6)].map((_, i) => (
								<div key={i} className="d-flex gap-3 align-items-center mb-3">
									<div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, flex: 2 }} />
									<div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, flex: 1.5 }} />
									<div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, flex: 1.5 }} />
									<div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, flex: 1.5 }} />
									<div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, flex: 1 }} />
									<div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, flex: 1 }} />
								</div>
							))}
						</div>
					) : (
						<DataGrid
							columns={columns}
							rows={filteredRows}
							defaultRowsPerPage={10}
							stickyHeader
							getRowId={(row) => row.id}
						/>
					)}
				</div>
			</div>

			<DeleteModel
				isOpen={Boolean(deleteTarget)}
				title="Delete Incentive Rate"
				entityLabel="incentive rate"
				itemName={deleteTarget ? `${deleteTarget.organization_name} · ${deleteTarget.cpt_code} · ${deleteTarget.insurance} · ${deleteTarget.facility}` : ''}
				description="You are about to remove this incentive rate. Future payroll runs will no longer match it."
				confirmText="Delete Rate"
				isLoading={deleteLoading}
				errorMessage={deleteError}
				onClose={handleDeleteClose}
				onConfirm={handleDeleteConfirm}
			/>
		</>
	);
}
